import { useCallback, useEffect, useState } from "react";
import { Copy, ExternalLink, Eye, KeyRound, Pencil, ShieldAlert, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, CRED_TYPES, errMsg } from "../lib/api";
import { EmptyState } from "./ui-bits";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { CredentialForm } from "./SimpleForms";

async function copyText(text, msg = "Kopiert") {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(msg);
  } catch {
    toast.error("Kopieren fehlgeschlagen");
  }
}

export function CredentialList({ projectId, clientId, projects = [], clients = [], defaultProjectId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [revealFor, setRevealFor] = useState(null);
  const [revealPw, setRevealPw] = useState("");
  const [revealed, setRevealed] = useState({});
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (projectId) params.project_id = projectId;
      if (clientId) params.client_id = clientId;
      const { data } = await api.get("/credentials", { params });
      setItems(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [projectId, clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const doReveal = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post(`/credentials/${revealFor.id}/reveal`, { password: revealPw });
      setRevealed((r) => ({ ...r, [revealFor.id]: data.password }));
      setRevealFor(null);
      setRevealPw("");
      toast.success("Passwort entschlüsselt");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setConfirming(false);
    }
  };

  const remove = async (cred) => {
    if (!window.confirm(`Zugang „${cred.label}" wirklich löschen?`)) return;
    try {
      await api.delete(`/credentials/${cred.id}`);
      toast.success("Zugang gelöscht");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800" data-testid="credentials-warning">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          Sensible Zugangsdaten – vertraulich behandeln.
        </p>
        <Button
          data-testid="credential-add-button"
          onClick={() => { setEditing(null); setFormOpen(true); }}
          className="rounded-xl bg-[#1D225C] text-white hover:bg-[#2a3070]"
        >
          + Neuer Zugang
        </Button>
      </div>

      {loading ? (
        <p className="py-8 text-center text-sm text-[#777]">Laden…</p>
      ) : items.length === 0 ? (
        <EmptyState icon={KeyRound} title="Keine Zugänge gespeichert" hint="WordPress, Hosting, FTP & Co. sicher und verschlüsselt ablegen." testid="credentials-empty" />
      ) : (
        <div className="space-y-3">
          {items.map((c) => (
            <div
              key={c.id}
              data-testid={`credential-card-${c.id}`}
              className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-2xl border border-[#1D225C]/10 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
            >
              <span className="rounded-lg bg-[#1D225C]/5 px-2.5 py-1 text-[11px] font-semibold text-[#1D225C]">
                {CRED_TYPES[c.service_type] || c.service_type}
              </span>
              <div className="min-w-40 flex-1">
                <p className="text-sm font-semibold text-[#1D225C]">{c.label}</p>
                <p className="truncate text-xs text-[#777]">
                  {[c.client_name, c.project_name].filter(Boolean).join(" · ") || "—"}
                </p>
              </div>
              {c.login_url && (
                <a
                  data-testid={`credential-url-${c.id}`}
                  href={c.login_url.startsWith("http") ? c.login_url : `https://${c.login_url}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1 text-xs font-medium text-[#B88A4A] hover:underline"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Login-Seite
                </a>
              )}
              {c.username && (
                <button
                  data-testid={`credential-copy-user-${c.id}`}
                  onClick={() => copyText(c.username, "Benutzername kopiert")}
                  className="flex items-center gap-1.5 rounded-lg border border-[#1D225C]/10 px-2.5 py-1.5 text-xs text-[#1D225C] transition-colors hover:border-[#B88A4A]/50"
                >
                  <span className="max-w-36 truncate">{c.username}</span>
                  <Copy className="h-3.5 w-3.5 text-[#B88A4A]" />
                </button>
              )}
              <div className="flex items-center gap-1.5">
                <span className="rounded-lg border border-[#1D225C]/10 bg-[#F7F6F2] px-2.5 py-1.5 font-mono2 text-xs text-[#1D225C]" data-testid={`credential-password-${c.id}`}>
                  {revealed[c.id] || "••••••••••"}
                </span>
                {revealed[c.id] ? (
                  <button data-testid={`credential-copy-pw-${c.id}`} onClick={() => copyText(revealed[c.id], "Passwort kopiert")} className="rounded-lg p-1.5 text-[#B88A4A] hover:bg-[#B88A4A]/10">
                    <Copy className="h-4 w-4" />
                  </button>
                ) : (
                  <button data-testid={`credential-reveal-${c.id}`} onClick={() => { setRevealFor(c); setRevealPw(""); }} className="rounded-lg p-1.5 text-[#1D225C] hover:bg-[#1D225C]/5">
                    <Eye className="h-4 w-4" />
                  </button>
                )}
                <button data-testid={`credential-edit-${c.id}`} onClick={() => { setEditing(c); setFormOpen(true); }} className="rounded-lg p-1.5 text-[#1D225C] hover:bg-[#1D225C]/5">
                  <Pencil className="h-4 w-4" />
                </button>
                <button data-testid={`credential-delete-${c.id}`} onClick={() => remove(c)} className="rounded-lg p-1.5 text-red-500 hover:bg-red-50">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <CredentialForm
        open={formOpen}
        onOpenChange={setFormOpen}
        credential={editing}
        projects={projects}
        clients={clients}
        defaultProjectId={defaultProjectId}
        onSaved={load}
      />

      <Dialog open={!!revealFor} onOpenChange={(v) => !v && setRevealFor(null)}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="credential-reveal-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-lg text-[#1D225C]">Identität bestätigen</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#777]">
            Bitte bestätige dein eigenes Login-Passwort, um „{revealFor?.label}" anzuzeigen.
          </p>
          <div>
            <Label>Dein Passwort</Label>
            <Input
              data-testid="credential-reveal-password-input"
              type="password"
              value={revealPw}
              onChange={(e) => setRevealPw(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doReveal()}
              className="mt-1.5"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="credential-reveal-cancel" onClick={() => setRevealFor(null)} className="rounded-xl">Abbrechen</Button>
            <Button data-testid="credential-reveal-submit" onClick={doReveal} disabled={confirming || !revealPw} className="rounded-xl bg-[#1D225C] text-white hover:bg-[#2a3070]">
              {confirming ? "Prüfen…" : "Anzeigen"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
